/**
 * Hooks para administración del sync de vencimientos fiscales (ARCA/COMARB).
 * ORR-100 Fase 1.
 *
 * - Lectura (audit logs, última sync, kill switch): TanStack Query
 * - Escritura (sync ahora, marcar revisado, toggle kill): mutations con Auth0 JWT
 *   contra Netlify Functions o Supabase directo según corresponda.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import {
  listarRecientes,
  obtenerUltimaSync,
} from '@/core/services/vencimientosSyncService'
import type { SyncSource, SyncStatus, SyncAuditLog } from '@/types/sync'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// ============================================================================
// Query keys
// ============================================================================

export const syncVencimientosKeys = {
  all: ['sync-vencimientos'] as const,
  auditLogs: (filtros?: { source?: SyncSource; status?: SyncStatus; limit?: number }) =>
    [...syncVencimientosKeys.all, 'audit-logs', filtros] as const,
  ultimaSync: (source: SyncSource) => [...syncVencimientosKeys.all, 'ultima', source] as const,
  killSwitch: () => [...syncVencimientosKeys.all, 'kill-switch'] as const,
  esSuperadmin: (email: string) => [...syncVencimientosKeys.all, 'es-superadmin', email] as const,
}

// ============================================================================
// Queries (lectura)
// ============================================================================

interface UseSyncAuditLogsOpts {
  source?: SyncSource
  status?: SyncStatus
  limit?: number
}

export function useSyncAuditLogs(filtros: UseSyncAuditLogsOpts = {}) {
  const { supabase, isLoading } = useSupabase()
  return useQuery<SyncAuditLog[]>({
    queryKey: syncVencimientosKeys.auditLogs(filtros),
    queryFn: () => listarRecientes(supabase!, filtros),
    enabled: !!supabase && !isLoading,
    staleTime: 30_000,
    // ORR-132: mientras haya un sync 'running' (corre en Background Function),
    // refrescar cada 3s para reflejar el resultado en vivo.
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === 'running') ? 3_000 : false,
  })
}

export function useUltimaSync(source: SyncSource) {
  const { supabase, isLoading } = useSupabase()
  return useQuery<SyncAuditLog | null>({
    queryKey: syncVencimientosKeys.ultimaSync(source),
    queryFn: () => obtenerUltimaSync(supabase!, source),
    enabled: !!supabase && !isLoading,
    staleTime: 60_000,
  })
}

/**
 * Determina si el usuario actual está en la whitelist de superadmins
 * (app_settings.sync_arca_superadmin_emails). Usado para mostrar el
 * KillSwitchCard — los permisos de rol (toggleKillSwitch) están false
 * para todos por design; el control real es la whitelist.
 * Compara el email de la fila resuelta por sub, el mismo dato que usa
 * sync-toggle-kill-switch, y no el que trae Auth0 (ORR-479).
 */
export function useEsSuperadmin() {
  const { usuario } = useOrganizacion()
  const { supabase, isLoading } = useSupabase()
  const email = usuario?.email ?? ''
  return useQuery<boolean>({
    queryKey: syncVencimientosKeys.esSuperadmin(email),
    queryFn: async () => {
      const sb = supabase as unknown as SupabaseClient<Database> & {
        from: (table: string) => ReturnType<SupabaseClient<Database>['from']>
      }
      const { data } = await sb
        .from('app_settings')
        .select('value')
        .eq('key', 'sync_arca_superadmin_emails')
        .maybeSingle()
      const value = (data as { value?: unknown } | null)?.value
      if (!Array.isArray(value)) return false
      return value.includes(email)
    },
    enabled: !!supabase && !isLoading && !!email,
    staleTime: 5 * 60_000, // 5 min, casi inmutable
  })
}

/**
 * Estado actual del kill switch (app_settings.sync_arca_enabled).
 */
export function useKillSwitchStatus() {
  const { supabase, isLoading } = useSupabase()
  return useQuery<{ enabled: boolean }>({
    queryKey: syncVencimientosKeys.killSwitch(),
    queryFn: async () => {
      const sb = supabase as unknown as SupabaseClient<Database> & {
        from: (table: string) => ReturnType<SupabaseClient<Database>['from']>
      }
      const { data } = await sb
        .from('app_settings')
        .select('value')
        .eq('key', 'sync_arca_enabled')
        .maybeSingle()
      const value = (data as { value?: unknown } | null)?.value
      return { enabled: value !== false } // default true
    },
    enabled: !!supabase && !isLoading,
    staleTime: 30_000,
  })
}

// ============================================================================
// Mutations
// ============================================================================

interface SyncManualResponse {
  // ORR-132: el endpoint ahora despacha una Background Function y devuelve 202
  // con status 'dispatched'. El resultado real (success/partial/error/skipped)
  // se observa por polling de sync_audit_log, no en esta respuesta.
  status: SyncStatus | 'dispatched'
}

export function useDispararSyncManual() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  return useMutation<SyncManualResponse, Error, { source: SyncSource }>({
    mutationFn: async () => {
      assertPermiso(puede, 'dispararSyncManual')
      const token = await getAccessTokenSilently()
      const response = await fetch('/.netlify/functions/sync-arca-manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error ?? `Error ${response.status} al disparar sync`)
      }
      return data as SyncManualResponse
    },
    onSuccess: () => {
      // El sync corre en segundo plano; refrescamos para empezar a ver el 'running'
      // y el polling de useSyncAuditLogs hace el resto.
      queryClient.invalidateQueries({ queryKey: syncVencimientosKeys.all })
      toast.info('Sync iniciado en segundo plano. El resultado aparece en unos minutos.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

/**
 * Marcar audit log como revisado. RLS de sync_audit_log reserva UPDATE a
 * service_role (Codex P2), por lo que el hook va via Netlify function con
 * Auth0 JWT en vez de UPDATE directo al cliente Supabase.
 */
export function useMarcarRevisado() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  return useMutation<void, Error, { auditLogId: string }>({
    mutationFn: async ({ auditLogId }) => {
      assertPermiso(puede, 'marcarRevisadoSync')
      const token = await getAccessTokenSilently()
      const response = await fetch('/.netlify/functions/sync-marcar-revisado', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ auditLogId }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error ?? `Error ${response.status} al marcar revisado`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: syncVencimientosKeys.all })
      toast.success('Marcado como revisado')
    },
    onError: (err) => {
      toast.error(`Error al marcar revisado: ${err.message}`)
    },
  })
}

/**
 * Toggle del kill switch. RLS de app_settings bloquea UPDATE a authenticated,
 * por lo que el hook va via Netlify function con Auth0 JWT. La function valida
 * que el usuario esté en la whitelist de superadmins antes de aplicar el UPDATE.
 */
export function useToggleKillSwitch() {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation<{ enabled: boolean }, Error, { enabled: boolean }>({
    mutationFn: async ({ enabled }) => {
      const token = await getAccessTokenSilently()
      const response = await fetch('/.netlify/functions/sync-toggle-kill-switch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled }),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error ?? `Error ${response.status} al togglear kill switch`)
      }
      return { enabled }
    },
    onSuccess: ({ enabled }) => {
      queryClient.invalidateQueries({ queryKey: syncVencimientosKeys.killSwitch() })
      toast.success(enabled ? 'Sync habilitado' : 'Sync deshabilitado (kill switch ON)')
    },
    onError: (err) => {
      toast.error(`Error: ${err.message}`)
    },
  })
}
